import { useState, useCallback, useRef, useEffect } from 'react';
import { Video } from './useLectureData';
import { useAdminFetch } from '../api/fetch';
import { formatDate } from '../util/datetime';

// 秒数をhh:mm:ss形式に変換する関数
const formatSecondsToTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

interface DownloadData {
  id: string;
  email: string;
  lectureName: string;
  lectureStatus: string;
  videos: Video[];
}

interface CSVData {
  [key: string]: any;
}

interface CSVHeader {
  label: string;
  key: string;
}

export const useCSVDownload = () => {
  const [csvHeaders, setCsvHeaders] = useState<CSVHeader[]>([]);
  const [csvData, setCsvData] = useState<CSVData[]>([]);
  const csvLink = useRef<any>();
  const { get } = useAdminFetch();

  const downloadCSV = useCallback(async () => {
    try {
      const res = await get<{ downloadData: DownloadData[] }>('/api/admin/csv');
      return res.downloadData;
    } catch (error) {
      console.error("Can't get csv download from db properly:", error);
      return null;
    }
  }, [get]);

  const convertToCSV = useCallback((data: DownloadData[]) => {
    let jsonData: CSVData[] = [];
    let maxVideosLength = 0;
    
    data.forEach((item) => {
      if (item.videos.length > maxVideosLength) {
        maxVideosLength = item.videos.length;
      }
    });

    let headers: CSVHeader[] = [
      { label: "ユーザーID", key: "id" },
      { label: "メールアドレス", key: "email" },
      { label: "講座名", key: "lectureName" },
      { label: "講座状況", key: "lectureStatus" },
    ];

    for (let i = 0; i < maxVideosLength; i++) {
      headers.push({ label: `動画${i + 1}名`, key: `video${i + 1}_name` });
      headers.push({
        label: `動画${i + 1}視聴開始時刻`,
        key: `video${i + 1}_startTime`,
      });
      headers.push({
        label: `動画${i + 1}視聴終了時刻`,
        key: `video${i + 1}_endTime`,
      });
      headers.push({
        label: `動画${i + 1}視聴合計時間`,
        key: `video${i + 1}_watchedSeconds`,
      });
      headers.push({
        label: `動画${i + 1}テスト結果`,
        key: `video${i + 1}_testResult`,
      });
      headers.push({
        label: `動画${i + 1}テスト回答時刻`,
        key: `video${i + 1}_testTime`,
      });
    }

    data.forEach((item) => {
      let jsonItem: CSVData = {
        id: item.id,
        email: item.email,
        lectureName: item.lectureName,
        lectureStatus: item.lectureStatus,
      };

      item.videos.forEach((video, index) => {
        jsonItem[`video${index + 1}_name`] = video.name || video.videoName;
        jsonItem[`video${index + 1}_startTime`] =
          video.startTime !== "-" && video.startTime ? formatDate(video.startTime, true) : "-";
        jsonItem[`video${index + 1}_endTime`] =
          video.endTime !== "-" && video.endTime ? formatDate(video.endTime, true) : "-";
        jsonItem[`video${index + 1}_watchedSeconds`] = formatSecondsToTime(video.watchedSeconds || 0);
        jsonItem[`video${index + 1}_testResult`] = video.testResult || "-";
        jsonItem[`video${index + 1}_testTime`] =
          video.testTime !== "-" && video.testTime ? formatDate(video.testTime, true) : "-";
      });

      jsonData.push(jsonItem);
    });

    return { jsonData, headers };
  }, []);

  const handleDownloadCSV = useCallback(async () => {
    const downloadData = await downloadCSV();
    if (downloadData) {
      const { jsonData, headers } = convertToCSV(downloadData);
      setCsvHeaders(headers);
      setCsvData(jsonData);
    }
  }, [downloadCSV, convertToCSV]);

  useEffect(() => {
    // 受講者がいないとcsvダウンロードができない
    if (csvHeaders.length > 0 && csvData.length > 0) {
      csvLink.current?.link?.click();
    }
  }, [csvHeaders, csvData]);

  return {
    csvHeaders,
    csvData,
    csvLink,
    handleDownloadCSV,
  };
};
